import React, { useEffect, useRef, useState, useCallback } from 'react';
import { config } from '../../config';
import { openPaddleInlineCheckout } from '../../utils/paddle';
import PaymentSuccessView from '../Billing/PaymentSuccessView';
import './scss/PaddlePaymentForm.scss';

// Poll cadence + budget for the post-payment activation check. Paddle grants
// entitlements asynchronously via the `transaction.completed` webhook, so after
// the inline checkout reports completion we poll verify-activation until the
// webhook has landed (or we give up softly — the webhook still arrives).
const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 20000;

// The one-screen lifecycle:
//   'preparing'  — creating the backend transaction
//   'ready'      — transaction created; inline card fields mounted + open
//   'verifying'  — Paddle reported checkout.completed; polling for activation
//   'success'    — activation confirmed; shows the verified success view
//   'pending'    — verify timed out; soft "activating shortly" message
//   'error'      — create-transaction or Paddle checkout error
const STATE = {
  PREPARING: 'preparing',
  READY: 'ready',
  VERIFYING: 'verifying',
  SUCCESS: 'success',
  PENDING: 'pending',
  ERROR: 'error',
};

/**
 * Reusable one-screen Paddle payment surface.
 *
 * Renders the plan summary and Paddle's inline (PCI-compliant) card fields
 * together, with a single implicit "pay" action inside the Paddle frame — no
 * separate "confirm then reveal" step. After Paddle reports completion it polls
 * the backend verify-activation endpoint before declaring success, since Paddle
 * activation is async (webhook-driven).
 *
 * The parent owns which backend endpoint creates the transaction via the
 * `createTransaction` prop, keeping this component decoupled from the surface.
 *
 * @param {Object} props
 * @param {'lifetime'|'seat'|'white_label'} props.type
 * @param {() => Promise<{ transactionId: string, amountDollars?: number }>} props.createTransaction
 * @param {boolean} [props.showSummary=true] Render the internal plan summary.
 *   Pass false when the parent already renders its own canonical summary (e.g.
 *   the seat checkout's richer `.billing-summary`) to avoid a duplicate.
 * @param {React.ReactNode} [props.summary]  Pre-rendered plan summary node.
 * @param {string} [props.title]             Summary title (used if `summary` absent).
 * @param {number|string} [props.amountDollars]  Total (used if `summary` absent).
 * @param {Array<{label:string,value:string}>} [props.lineItems]  Summary rows.
 * @param {string} [props.billingNote]        Small note under the total.
 * @param {'lifetime_purchase'|'seat_subscription'|'white_label_addon'} props.verifyType
 * @param {string} [props.workspaceId]        Workspace shortId (omit for white_label).
 * @param {string} props.frameTarget          Unique class for the inline mount.
 * @param {number} [props.successSeats]       Seat count to show in the success view.
 * @param {string} [props.successEmail]       Buyer email for the receipt note.
 * @param {() => void} props.onSuccess        Fired when the user clicks "Go to billing".
 * @param {() => void} props.onCancel
 */
const PaddlePaymentForm = ({
  type,
  createTransaction,
  showSummary = true,
  summary,
  title,
  amountDollars,
  lineItems,
  billingNote,
  verifyType,
  workspaceId,
  frameTarget,
  successSeats,
  successEmail,
  onSuccess,
  onCancel,
}) => {
  const [status, setStatus] = useState(STATE.PREPARING);
  const [transactionId, setTransactionId] = useState(null);
  const [resolvedAmount, setResolvedAmount] = useState(amountDollars);
  const [activation, setActivation] = useState(null);
  const [error, setError] = useState('');

  // Guards so we don't double-create a transaction (StrictMode / re-render) or
  // keep polling after unmount.
  const createStartedRef = useRef(false);
  const openedRef = useRef(false);
  const pollTimerRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, []);

  // Step 1 — create the backend transaction on mount. The inline container is
  // rendered only once we have a transactionId (see the open effect below), so
  // the div always exists before Paddle.Checkout.open() looks for it.
  const runCreate = useCallback(async () => {
    setError('');
    setStatus(STATE.PREPARING);
    openedRef.current = false;
    try {
      const result = await createTransaction();
      if (!mountedRef.current) return;
      if (!result || !result.transactionId) {
        // Surface the backend's actual reason (e.g. "already on a paid plan")
        // when it returned an error payload instead of a transaction.
        throw new Error(result?.error || result?.message || 'No transaction returned. Please try again.');
      }
      if (result.amountDollars != null) setResolvedAmount(result.amountDollars);
      setTransactionId(result.transactionId);
      setStatus(STATE.READY);
    } catch (err) {
      if (!mountedRef.current) return;
      console.error('Paddle create-transaction failed:', err);
      setError(err.message || 'Failed to prepare checkout. Please try again.');
      setStatus(STATE.ERROR);
    }
  }, [createTransaction]);

  useEffect(() => {
    if (createStartedRef.current) return;
    createStartedRef.current = true;
    runCreate();
  }, [runCreate]);

  // Poll verify-activation after Paddle reports checkout.completed. Resolves to
  // onSuccess() on activation, or a soft "pending" state on timeout.
  const startVerifying = useCallback(() => {
    if (!mountedRef.current) return;
    setStatus(STATE.VERIFYING);
    const deadline = Date.now() + POLL_TIMEOUT_MS;

    const params = new URLSearchParams({ type: verifyType });
    // white_label uses the auth'd user server-side, so workspaceId is omitted.
    if (workspaceId) params.set('workspaceId', workspaceId);
    const verifyUrl = `${config.API_URI}/v1/paddle/verify-activation?${params.toString()}`;

    const poll = async () => {
      try {
        const res = await fetch(verifyUrl, { credentials: 'include' });
        const data = res.ok ? await res.json() : null;
        if (!mountedRef.current) return;

        if (data && data.activated) {
          // Capture what we actually confirmed so the success view reflects the
          // real plan — not a hard-coded label.
          setActivation(data);
          setStatus(STATE.SUCCESS);
          return;
        }
      } catch (err) {
        // Transient network error — keep polling until the deadline.
        console.error('verify-activation poll failed:', err);
      }

      if (!mountedRef.current) return;

      if (Date.now() >= deadline) {
        // Soft landing: payment succeeded, entitlement just hasn't propagated
        // yet. The webhook will still activate it — never imply failure.
        setStatus(STATE.PENDING);
        return;
      }
      pollTimerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
    };

    poll();
  }, [verifyType, workspaceId]);

  // Step 2 — mount-before-open: once transactionId is set the summary + the
  // `frameTarget` div are rendered, so this effect can safely open Paddle inline
  // into that (now-present) container.
  useEffect(() => {
    if (status !== STATE.READY || !transactionId || openedRef.current) return;
    openedRef.current = true;

    const theme =
      document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark';

    try {
      openPaddleInlineCheckout({
        transactionId,
        frameTarget,
        theme,
        onSuccess: () => startVerifying(),
        onClose: () => {
          // Paddle's inline frame doesn't surface a user-visible close; treat as
          // cancel only if we're still on the payment step.
          if (mountedRef.current && status === STATE.READY) {
            onCancel?.();
          }
        },
        onError: (err) => {
          console.error('Paddle checkout error:', err);
          if (!mountedRef.current) return;
          setError('Payment failed. Please check your details and try again.');
          setStatus(STATE.ERROR);
        },
      });
    } catch (err) {
      console.error('Failed to open Paddle inline checkout:', err);
      if (mountedRef.current) {
        setError('Unable to open the payment form. Please try again.');
        setStatus(STATE.ERROR);
      }
    }
  }, [status, transactionId, frameTarget, startVerifying, onCancel]);

  const handleRetry = () => {
    createStartedRef.current = false;
    setTransactionId(null);
    runCreate().finally(() => {
      createStartedRef.current = true;
    });
  };

  const renderSummary = () => {
    if (summary) return summary;
    return (
      <div className="paddle-pay-summary-body">
        {title && <div className="paddle-pay-summary-title">{title}</div>}
        {Array.isArray(lineItems) && lineItems.length > 0 && (
          <ul className="paddle-pay-line-items">
            {lineItems.map((item, idx) => (
              <li key={idx}>
                <span className="paddle-pay-line-label">{item.label}</span>
                <span className="paddle-pay-line-value">{item.value}</span>
              </li>
            ))}
          </ul>
        )}
        {resolvedAmount != null && (
          <div className="paddle-pay-total">
            <span>Total</span>
            <span>${resolvedAmount} USD</span>
          </div>
        )}
        {billingNote && <p className="paddle-pay-billing-note">{billingNote}</p>}
      </div>
    );
  };

  // Success stands alone — no plan summary above it — so the confirmed outcome
  // reads as a calm, finished screen rather than a still-active checkout.
  if (status === STATE.SUCCESS) {
    return (
      <div className={`paddle-pay paddle-pay--${type} paddle-pay--success`}>
        <PaymentSuccessView
          planType={activation?.planType}
          billingCycle={activation?.billingCycle}
          seats={successSeats}
          whiteLabel={type === 'white_label' || verifyType === 'white_label_addon'}
          email={successEmail}
          onDone={() => onSuccess?.()}
        />
      </div>
    );
  }

  return (
    <div className={`paddle-pay paddle-pay--${type}`}>
      {showSummary && <div className="paddle-pay-summary">{renderSummary()}</div>}

      {status === STATE.ERROR ? (
        <div className="paddle-pay-error-state">
          <div className="paddle-pay-error">{error}</div>
          <div className="paddle-pay-actions">
            <button type="button" className="paddle-pay-btn paddle-pay-btn--secondary" onClick={onCancel}>
              Cancel
            </button>
            <button type="button" className="paddle-pay-btn paddle-pay-btn--primary" onClick={handleRetry}>
              Try again
            </button>
          </div>
        </div>
      ) : status === STATE.VERIFYING ? (
        <div className="paddle-pay-status-state">
          <div className="paddle-pay-spinner" aria-hidden="true" />
          <p className="paddle-pay-status-text">Confirming your activation…</p>
        </div>
      ) : status === STATE.PENDING ? (
        <div className="paddle-pay-status-state">
          <p className="paddle-pay-status-text">
            Payment received. Your plan is activating and will be ready in a moment.
          </p>
          <div className="paddle-pay-actions">
            <button
              type="button"
              className="paddle-pay-btn paddle-pay-btn--primary"
              onClick={() => onSuccess?.()}
            >
              Go to billing
            </button>
          </div>
        </div>
      ) : status === STATE.PREPARING ? (
        // While preparing, show ONLY a loading indicator — no Cancel button,
        // since there's nothing to cancel until the payment form is ready.
        <div className="paddle-pay-status-state">
          <div className="paddle-pay-spinner" aria-hidden="true" />
          <p className="paddle-pay-status-text">Loading payment details…</p>
        </div>
      ) : (
        <>
          {/* Paddle mounts its card fields into this container. It's rendered as
              soon as we have a transactionId, before the open effect runs. */}
          {transactionId && <div className={`paddle-pay-frame ${frameTarget}`} />}

          <div className="paddle-pay-actions">
            <button
              type="button"
              className="paddle-pay-btn paddle-pay-btn--secondary"
              onClick={onCancel}
            >
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default PaddlePaymentForm;
